import { defn } from './mod.js';

/**
 * A function version of `typeof` operator with improvements
 *
 * Returns the same as `typeof x` with improvement(s):
 * - `getType(null)` will return `"null"` instead of `"object"`
 *
 * @param {*} x The value to get the type of
 * @returns {string} The type of the value
 */
export const getType = x => (x !== null ? typeof x : 'null');

/**
 * Test whether a value is of a specific type
 *
 * For this function, the concept of a type is broadened to cover BOTH `typeof`
 * and `instanceof` checks with a single function. Uses {@link getType} return
 * values for string types.
 *
 * This function is curried. If you pass only 1 argument, it will return a
 * function that accepts the second argument and returns the boolean result.
 *
 * @param  {string|Function} type A {@link getType} returned type string or a constructor function
 * @param  {*} value A value to test against the type
 * @returns {boolean} true if {@link value} is of {@link type}
 */
export const isType = (...args) => {
	const [type, value] = args,
		isTypeof = typeof type !== 'function',
		fn =
			args.length === 0 ? () => false
			: isTypeof ? x => getType(x) === String(type)
			: x => x instanceof type,
		typeName = isTypeof ? type.charAt(0).toUpperCase().concat(type.slice(1)) : type.name;

	return args.length <= 1 ? defn(`is${typeName}`, fn) : fn(value);
};

export const isUndef = isType('undefined');
export const isNull = isType('null');
export const isNullish = x => isNull(x) || isUndef(x);
export const isBoolean = x => isType('boolean', x) || isType(Boolean, x);
export const isString = x => isType('string', x) || isType(String, x);
export const isNumber = x => isType('number', x) || isType(Number, x);
export const isBigInt = isType('bigint');
export const isSymbol = isType('symbol');
export const isFn = isType('function');
export const isObj = isType('object');
export const isNullObj = x => isObj(x) && Object.getPrototypeOf(x) === null;
export const isArray = Array.isArray;
export const isArrayLike = x =>
	isArray(x) || (!isNullish(x) && isNumber(x.length) && x.length >= 0);
export const isDate = isType(Date);
